
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AgentCapability, agent_capability_type } from '@/types/agentic';
import { createCapability, updateCapability } from '@/services/agentic/capabilityService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import JSONEditor from '@/components/ui/json-editor';

// Define the form schema using Zod
const formSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  description: z.string().optional(),
  capability_type: z.enum(['course_management', 'user_management', 'content_generation', 'notification', 'assessment', 'data_analysis', 'scheduling']),
  config: z.record(z.any()).default({
    parameters: {
      type: 'object',
      properties: {},
      required: []
    }
  })
});

interface CapabilityFormProps {
  initialData?: AgentCapability | null;
  onSuccess: (capability: AgentCapability) => void;
  onCancel: () => void;
}

const CapabilityForm: React.FC<CapabilityFormProps> = ({ 
  initialData, 
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData ? {
      name: initialData.name,
      description: initialData.description || '',
      capability_type: initialData.capability_type,
      config: initialData.config
    } : {
      name: '',
      description: '',
      capability_type: 'content_generation',
      config: {
        parameters: {
          type: 'object',
          properties: {},
          required: []
        }
      }
    }
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setLoading(true);
      
      // Create or update the capability
      let capability: AgentCapability;
      
      if (initialData) {
        capability = await updateCapability(initialData.id, {
          name: values.name,
          description: values.description || null,
          capability_type: values.capability_type,
          config: values.config
        });
        toast.success('Capability updated successfully');
      } else {
        capability = await createCapability({
          name: values.name,
          description: values.description || null,
          capability_type: values.capability_type,
          config: values.config
        });
        toast.success('Capability created successfully');
      }
      
      onSuccess(capability);
    } catch (error) {
      console.error('Error saving capability:', error);
      toast.error('Failed to save capability');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to create example config based on capability type
  const generateExampleConfig = (type: agent_capability_type) => {
    let exampleConfig = {
      parameters: {
        type: 'object',
        properties: {},
        required: []
      }
    };

    switch (type) {
      case 'content_generation':
        exampleConfig.parameters.properties = {
          prompt: { type: 'string', description: 'The prompt to generate content from' },
          style: { type: 'string', description: 'The content style (formal, casual, technical, etc)' }
        };
        exampleConfig.parameters.required = ['prompt'];
        break;
      case 'user_management':
        exampleConfig.parameters.properties = {
          user_id: { type: 'string', description: 'The user ID to manage' },
          action: { type: 'string', description: 'The action to perform' }
        };
        exampleConfig.parameters.required = ['user_id', 'action'];
        break;
      // Add more examples for other capability types
    }

    form.setValue('config', exampleConfig);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          {initialData ? 'Edit Capability' : 'Create New Capability'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Get User Data" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Retrieves user data based on user ID" 
                      {...field} 
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="capability_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Capability Type</FormLabel>
                  <Select 
                    onValueChange={(value: agent_capability_type) => {
                      field.onChange(value);
                      if (!initialData) {
                        generateExampleConfig(value as agent_capability_type);
                      }
                    }}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select capability type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="course_management">Course Management</SelectItem>
                      <SelectItem value="user_management">User Management</SelectItem>
                      <SelectItem value="content_generation">Content Generation</SelectItem>
                      <SelectItem value="notification">Notification</SelectItem>
                      <SelectItem value="assessment">Assessment</SelectItem>
                      <SelectItem value="data_analysis">Data Analysis</SelectItem>
                      <SelectItem value="scheduling">Scheduling</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="config"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Configuration (JSON)</FormLabel>
                  <FormControl>
                    <JSONEditor
                      value={field.value}
                      onChange={(value) => {
                        try {
                          field.onChange(value);
                          setJsonError(null);
                        } catch (error) {
                          setJsonError('Invalid JSON configuration');
                        }
                      }}
                      height="300px"
                    />
                  </FormControl>
                  {jsonError && <p className="text-sm text-red-500">{jsonError}</p>}
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {initialData ? 'Update' : 'Create'} Capability
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default CapabilityForm;
